import React, { useState, useEffect, useMemo } from 'react';
import {
  CalendarDays, Briefcase, CheckCircle2, Plus, Minus, PieChart,
  ChevronRight, ChevronLeft, Search, Calendar, User, Clock, UserCircle2,
  RefreshCw,
} from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import DateInput from '../common/DateInput';
import CalendarRow from './CalendarRow';
import LeaveBalanceDetailModal from './LeaveBalanceDetailModal';
import LeaveRequestDetailModal from './LeaveRequestDetailModal';
import { getMonthRange } from '../../utils/calendarUtils';
import {
  useTeamLeaveBalances,
  useTeamLeaveRequests,
  useTeamAbsenceCalendar,
} from '../../hooks/manager_portal_hooks';
import { toast } from 'react-toastify';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const REQUESTS_PAGE_SIZE = 10;

// Label the request amount with its correct unit, matching CalendarRow's formatAmount.
const formatRequestAmount = (amount = 0, unit = 'Days') => {
  if (unit === 'Hours') {
    return `${amount} hr${amount === 1 ? '' : 's'}`;
  }
  return `${amount} day${amount === 1 ? '' : 's'}`;
};

const TabBar = ({ tabs, activeTab, onSelect, small = false }) => (
  <div className="border-b border-gray-200 mb-4 sm:mb-6">
    <nav className="-mb-px flex space-x-2 sm:space-x-4 overflow-x-auto" aria-label="Tabs">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab.id)}
            className={`whitespace-nowrap py-2 ${small ? 'px-2 sm:px-3 text-[10px] sm:text-xs' : 'sm:py-3 px-2 sm:px-4 text-xs sm:text-sm'} border-b-2 font-medium flex items-center gap-1.5 sm:gap-2 ${
              isActive
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className={small ? 'w-3 h-3' : 'w-4 h-4'} />
            {tab.label}
          </button>
        );
      })}
    </nav>
  </div>
);

const ManagerTeamLeave = () => {
  const { setHeader } = usePageHeader();
  const {
    rows: teamBalanceRows,
    balancesByMember,
    isLoading: balancesLoading,
    error: balancesError,
  } = useTeamLeaveBalances();
  const [activeLeaveTab, setActiveLeaveTab] = useState('approvals');
  const [selectedLeaveBalanceMember, setSelectedLeaveBalanceMember] = useState(null);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);

  // Approvals tab state
  const {
    requests,
    isLoading: requestsLoading,
    error: requestsError,
    approve,
    reject,
    isMutating,
    isRefreshing,
    refresh: refreshRequests,
  } = useTeamLeaveRequests();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // dateFilter comes from DateInput as a DD/MM/YYYY string, while r.startDate/r.endDate
  // are raw ISO date-time strings from the API — normalize both to a comparable
  // YYYY-MM-DD form before comparing, otherwise <=/>= compares them lexicographically.
  const toComparableDate = (value) => {
    if (!value) return null;
    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }
    return value.slice(0, 10);
  };

  const filteredDate = toComparableDate(dateFilter);

  const filteredRequests = useMemo(
    () =>
      requests.filter((r) => {
        const matchesSearch =
          (r.employeeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.leaveTypeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.employeeNo || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'All' || r.status === statusFilter;
        const matchesDate =
          !filteredDate ||
          (toComparableDate(r.startDate) <= filteredDate && toComparableDate(r.endDate) >= filteredDate);
        return matchesSearch && matchesStatus && matchesDate;
      }),
    [requests, searchTerm, statusFilter, filteredDate]
  );

  const totalRequestPages = Math.max(1, Math.ceil(filteredRequests.length / REQUESTS_PAGE_SIZE));
  const pagedRequests = useMemo(
    () =>
      filteredRequests.slice(
        (currentPage - 1) * REQUESTS_PAGE_SIZE,
        currentPage * REQUESTS_PAGE_SIZE
      ),
    [filteredRequests, currentPage]
  );

  // Reset to page 1 whenever the filtered set changes shape, so the user
  // isn't stranded on a now-out-of-range page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, filteredDate]);

  const handleApprove = async (id) => {
    const request = requests.find((r) => r.id === id);
    if (!request) return;
    try {
      await approve(request);
      toast.success(`Leave request for ${request.employeeName} approved.`);
    } catch (err) {
      toast.error(`Failed to approve request: ${err.message}`);
    }
  };

  const handleReject = async (id) => {
    const request = requests.find((r) => r.id === id);
    if (!request) return;
    try {
      await reject(request);
      toast.success(`Leave request for ${request.employeeName} rejected.`);
    } catch (err) {
      toast.error(`Failed to reject request: ${err.message}`);
    }
  };

  const formatLeaveDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const STATUS_BADGE = {
    Pending: 'bg-yellow-100 text-yellow-800',
    Approved: 'bg-green-100 text-green-800',
    Rejected: 'bg-red-100 text-red-800',
    Cancelled: 'bg-gray-100 text-gray-800',
  };

  // Month being viewed in the Manager Absence Calendar, navigated the same
  // way as the Calendar page (ManagerCalendar.jsx): prev/next/Today, one
  // month of data fetched at a time rather than an arbitrary date range.
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [expandedIds, setExpandedIds] = useState([]);

  const monthRange = useMemo(() => getMonthRange(calendarMonth), [calendarMonth]);

  // Live team absence data (derived from LeaveRequestsV2 + EssLeaveRequestHeaders
  // via useTeamLeaveRequests), filtered to the viewed month.
  const {
    teamAbsence,
    isLoading: absenceLoading,
    error: absenceError,
  } = useTeamAbsenceCalendar(monthRange);

  useEffect(() => {
    setHeader({
      icon: CalendarDays,
      title: 'Team Leave',
      subtitle: 'View and manage your team’s leave requests, balances, and absence calendar.',
    });
    return () => setHeader({ icon: null, title: '', subtitle: '' });
  }, [setHeader]);

  const goToPreviousMonth = () =>
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goToNextMonth = () =>
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  const goToToday = () => setCalendarMonth(new Date());

  const toggleExpanded = (id) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const leaveSubTabs = [
    { id: 'approvals', label: 'View Requests / Approvals', icon: CheckCircle2 },
    // { id: 'on-behalf', label: 'On Behalf Request', icon: Users },
    { id: 'calendar', label: 'Manager Absence Calendar', icon: CalendarDays },
    { id: 'balance', label: 'Team Leave Balance', icon: PieChart },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
        <TabBar tabs={leaveSubTabs} activeTab={activeLeaveTab} onSelect={setActiveLeaveTab} small />

        {activeLeaveTab === 'approvals' && (
          <>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 sm:mb-6 gap-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
                Work Items Assigned to Me
              </h2>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs sm:text-sm font-medium self-start md:self-auto">
                {filteredRequests.length} {statusFilter === 'All' ? 'Total' : statusFilter}
              </span>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 sm:mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees or leave type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="All">All Statuses</option>
              </select>
              <div className="flex items-center gap-2">
                <DateInput
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => refreshRequests()}
                  disabled={isRefreshing}
                  title="Refresh"
                  aria-label="Refresh leave requests"
                  className="p-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Request cards */}
            {requestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading leave requests...</p>
              </div>
            ) : requestsError ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-sm">Failed to load leave requests. Please try again.</p>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No leave requests found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pagedRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <User className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                            <span className="truncate">{request.employeeName}</span>
                          </h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded flex-shrink-0 ${STATUS_BADGE[request.status] ?? 'bg-gray-100 text-gray-800'}`}>
                            {request.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <button
                              type="button"
                              onClick={() => setSelectedLeaveRequest(request)}
                              className="text-indigo-600 hover:underline focus:outline-none text-left"
                            >
                              {formatLeaveDate(request.startDate)} – {formatLeaveDate(request.endDate)}
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span>{formatRequestAmount(request.days, request.unit)}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Type:</span> {request.leaveTypeName}
                          </div>
                        </div>
                        {request.reason && (
                          <p className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Reason:</span> {request.reason}
                          </p>
                        )}
                        <p className="mt-2 text-xs text-gray-500">
                          Requested on: {formatLeaveDate(request.requestedDate)}
                        </p>
                      </div>
                      {['Pending', 'Submitted', 'Waiting'].includes(request.status) && (
                        <div className="flex flex-col sm:flex-row gap-2 md:ml-4 md:flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleApprove(request.id)}
                            disabled={isMutating}
                            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(request.id)}
                            disabled={isMutating}
                            className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!requestsLoading && !requestsError && totalRequestPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalRequestPages}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(totalRequestPages, prev + 1))}
                    disabled={currentPage === totalRequestPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {activeLeaveTab === 'calendar' && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-indigo-600" />
              Manager Absence Calendar
            </h2>

            {/* Month Navigation — same pattern as the Calendar page */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h3 className="text-xl font-semibold text-gray-800 min-w-[180px] text-center">
                  {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                </h3>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <button
                type="button"
                onClick={goToToday}
                className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Today
              </button>
            </div>

            {/* Legend — same colors/labels as the Calendar page */}
            <div className="flex flex-wrap gap-4 mb-6 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                <span className="text-xs text-gray-600">Approved Leave</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
                <span className="text-xs text-gray-600">Pending Leave</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                <span className="text-xs text-gray-600">Rejected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
                <span className="text-xs text-gray-600">Cancelled</span>
              </div>
            </div>

            {absenceLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading team absences...</p>
              </div>
            ) : absenceError ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-sm">Failed to load team absences. Please try again.</p>
              </div>
            ) : teamAbsence.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm">No team absences found for the selected month.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamAbsence.map((employee) => {
                  const isExpanded = expandedIds.includes(employee.id);
                  return (
                    <div key={employee.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(employee.id)}
                        className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100"
                      >
                        <span className="flex items-center gap-2">
                          {isExpanded ? (
                            <Minus className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Plus className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="text-sm font-medium text-gray-800">{employee.name}</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {employee.entries.length} day{employee.entries.length === 1 ? '' : 's'}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="pt-2 pb-3 px-2 sm:px-3">
                          <CalendarRow
                            entries={employee.entries}
                            month={calendarMonth}
                            onSelectEntry={(entry) =>
                              setSelectedLeaveRequest({
                                id: entry.requestId,
                                employeeName: employee.name,
                                employeeNo: employee.personnelNumber,
                                startDate: entry.date,
                                endDate: entry.date,
                                days: entry.amount,
                                unit: entry.unit,
                                leaveTypeName: entry.type,
                                status: entry.status,
                                reason: entry.reason,
                                requestedDate: entry.date,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeLeaveTab === 'balance' && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-indigo-600" />
              Team Leave Balance
            </h2>
            <p className="text-sm text-gray-500 mb-4">View a team member's full leave balance breakdown.</p>
            {balancesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading team leave balances...</p>
              </div>
            ) : balancesError ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-sm">Failed to load leave balances. Please try again.</p>
              </div>
            ) : teamBalanceRows.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm">No leave balance data available for your team.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamBalanceRows.map((item) => (
                  <div
                    key={item.personnelNumber}
                    className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <UserCircle2 className="w-4 h-4 text-indigo-500" />
                      </div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedLeaveBalanceMember(item)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      View Details
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedLeaveBalanceMember && (
        <LeaveBalanceDetailModal
          member={selectedLeaveBalanceMember}
          balances={balancesByMember[selectedLeaveBalanceMember.personnelNumber] ?? []}
          onClose={() => setSelectedLeaveBalanceMember(null)}
        />
      )}

      {selectedLeaveRequest && (
        <LeaveRequestDetailModal
          request={selectedLeaveRequest}
          onClose={() => setSelectedLeaveRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export default ManagerTeamLeave;
